/**
 * Outil « Bataille navale du repère » (Boîte à outils, 6e → 4e).
 *
 * Trois jeux pour lire et placer des coordonnées, abscisse PUIS ordonnée :
 * « Placer » un point demandé (au doigt, ou au clavier avec les flèches),
 * « Lire » les coordonnées d'un point affiché, et « Bataille » : couler une
 * flotte cachée en tirant sur des coordonnées. Les erreurs typiques sont
 * nommées (coordonnées inversées, signe de l'abscisse ou de l'ordonnée).
 * Repère de 0 à 8 en 6e, de −5 à 5 dès la 5e. Calculs : `battle.ts`.
 */

import { useRef, useState } from 'react';
import { schoolLevelRank } from '@jeraisonne/db/cards';
import { cn } from '@jeraisonne/ui';
import type { ToolProps } from '../../tool-types';
import { Note, PRIMARY_BUTTON, SECONDARY_BUTTON, Segmented, Stepper } from '../../ui/controls';
import { clamp, makeFrame } from '../../ui/frame';
import { seededRng } from '../../ui/random';
import { DragHandle, FIGURE_BG_CLASS, HaloText, PlotGrid } from '../../ui/svg-parts';
import { useDrag } from '../../ui/use-drag';
import {
  boundsFor,
  coordText,
  feedback,
  fire,
  isHit,
  placeFleet,
  randomPoint,
  sunkShips,
  type Bounds,
  type Feedback,
  type Pt,
  type Shot,
} from './battle';

type Mode = 'place' | 'read' | 'battle';

const SIZE = 320;
const TARGET = 'var(--color-primary-500)';
const CURSOR = 'var(--color-ink-900)';
const HIT = 'var(--color-error-500)';
const MISS = 'var(--color-ink-400)';

const n = (v: number) => String(v).replace('-', '−');
const goX = (x: number) =>
  x === 0
    ? 'abscisse 0 : reste sur l’axe vertical'
    : `va jusqu’à ${n(x)} sur l’axe horizontal (${x > 0 ? 'à droite' : 'à gauche'} de O)`;
const goY = (y: number) =>
  y === 0
    ? 'ordonnée 0 : reste à la hauteur de l’axe horizontal'
    : `monte ou descends jusqu’à ${n(y)} (${y > 0 ? 'au-dessus' : 'en dessous'} de l’axe horizontal)`;

function message(f: Feedback, target: Pt, given: Pt, verb: 'placé' | 'écrit'): string {
  const t = coordText(target);
  const g = coordText(given);
  switch (f) {
    case 'ok':
      return `Bravo : c’est bien le point ${t}.`;
    case 'swapped':
      return `Tu as ${verb} ${g} : abscisse et ordonnée sont inversées. On lit d’abord l’abscisse (axe horizontal), puis l’ordonnée (axe vertical).`;
    case 'signX':
      return `Tu as ${verb} ${g} : le signe de l’abscisse est faux. Abscisse positive à droite de l’origine, négative à gauche.`;
    case 'signY':
      return `Tu as ${verb} ${g} : le signe de l’ordonnée est faux. Ordonnée positive au-dessus de l’origine, négative en dessous.`;
    case 'signs':
      return `Tu as ${verb} ${g} : les deux signes sont faux.`;
    case 'wrong':
      return `Tu as ${verb} ${g}, pas ${t}. Pars de l’origine O : ${goX(target.x)}, puis ${goY(target.y)}.`;
  }
}

const SHOT_TEXT: Record<Shot, string> = {
  miss: 'dans l’eau',
  hit: 'touché !',
  sunk: 'touché, coulé !',
  repeat: 'déjà visé',
};

export function BattleshipTool({
  level,
  seed,
}: ToolProps & { level?: string | null; seed?: number }) {
  const rank = schoolLevelRank(level);
  const b: Bounds = boundsFor(rank);
  const vp = { xMin: b.min - 0.8, xMax: b.max + 0.8, yMin: b.min - 0.8, yMax: b.max + 0.8 };
  const frame = makeFrame(vp, SIZE);
  const rng = useRef(seededRng(seed ?? Date.now()));
  const svgRef = useRef<SVGSVGElement>(null);

  const [mode, setMode] = useState<Mode>('place');
  const [cursor, setCursor] = useState<Pt>({ x: 0, y: 0 });
  // Placer / Lire
  const [target, setTarget] = useState<Pt>(() => randomPoint(rng.current, b));
  const [answer, setAnswer] = useState<Pt>({ x: 0, y: 0 });
  const [given, setGiven] = useState<{ p: Pt; f: Feedback } | null>(null);
  const [reveal, setReveal] = useState(false);
  const [score, setScore] = useState({ first: 0, done: 0, tries: 0 });
  // Bataille
  const [fleet, setFleet] = useState(() => placeFleet(rng.current, b));
  const [shots, setShots] = useState<Pt[]>([]);
  const [lastShot, setLastShot] = useState<{ p: Pt; r: Shot } | null>(null);

  const inBounds = (p: Pt) => ({
    x: clamp(Math.round(p.x), b.min, b.max),
    y: clamp(Math.round(p.y), b.min, b.max),
  });
  const moveCursor = (p: Pt) => setCursor(inBounds(p));
  const usesCursor = mode === 'place' || mode === 'battle';
  const drag = useDrag<'c'>({
    svgRef,
    frame,
    ids: usesCursor ? ['c'] : [],
    pointOf: () => cursor,
    onDrag: (_, p) => moveCursor(p),
    onBackground: usesCursor ? (p) => moveCursor(p) : undefined,
    // moins d'une demi-unité : toucher un point voisin y pose le viseur au lieu d'attraper la poignée
    grabRadius: frame.unit * 0.45,
  });

  const nextTarget = () => {
    setTarget((old) => randomPoint(rng.current, b, [old]));
    setGiven(null);
    setReveal(false);
    setScore((s) => ({ ...s, tries: 0 }));
    setAnswer({ x: 0, y: 0 });
  };
  const check = (p: Pt) => {
    const f = feedback(target, p);
    setGiven({ p, f });
    setScore((s) =>
      f === 'ok'
        ? { first: s.first + (s.tries === 0 ? 1 : 0), done: s.done + 1, tries: s.tries + 1 }
        : { ...s, tries: s.tries + 1 },
    );
  };
  const shoot = () => {
    const r = fire(fleet, shots, cursor);
    setLastShot({ p: cursor, r });
    if (r !== 'repeat') setShots((s) => [...s, cursor]);
  };
  const sunk = sunkShips(fleet, shots);
  const gameOver = sunk.length === fleet.length;
  const solved = given?.f === 'ok';

  const px = (p: Pt) => ({ x: frame.tx(p.x), y: frame.ty(p.y) });
  const o = px({ x: 0, y: 0 });

  return (
    <div className="space-y-4">
      <Segmented
        ariaLabel="Jeu"
        value={mode}
        options={[
          { value: 'place', label: 'Placer' },
          { value: 'read', label: 'Lire' },
          { value: 'battle', label: 'Bataille' },
        ]}
        onChange={(m) => {
          setMode(m);
          // nouveau point : « Lire » ne doit pas montrer le point qu'on cherchait à placer
          nextTarget();
        }}
      />

      <section aria-live="polite" className="space-y-1" data-testid="bs-prompt">
        {mode === 'place' && (
          <>
            <p className="text-ink-900 text-base font-semibold">
              Place le point A{coordText(target)}, puis valide.
            </p>
            <p className="text-ink-600 text-sm">
              Touche le repère pour y poser le point à placer (ou déplace-le avec les flèches du
              clavier).
            </p>
          </>
        )}
        {mode === 'read' && (
          <p className="text-ink-900 text-base font-semibold">
            Quelles sont les coordonnées du point P ?
          </p>
        )}
        {mode === 'battle' && (
          <p className="text-ink-900 text-base font-semibold">
            Trois bateaux (de 3, 2 et 2 points) sont cachés sur les points du repère. Vise avec
            l’abscisse et l’ordonnée, puis tire.
          </p>
        )}
        {mode !== 'battle' && score.done > 0 && (
          <p className="text-ink-600 text-sm" data-testid="bs-score">
            {score.done} point{score.done > 1 ? 's' : ''} trouvé{score.done > 1 ? 's' : ''}, dont{' '}
            {score.first} du premier coup.
          </p>
        )}
      </section>

      <div className="mx-auto w-full max-w-[26rem]">
        <svg
          ref={svgRef}
          viewBox={`0 0 ${SIZE} ${SIZE}`}
          className={cn(
            'border-ink-100 block aspect-square w-full select-none rounded-2xl border',
            usesCursor ? 'touch-none' : '',
            FIGURE_BG_CLASS,
          )}
          role="group"
          aria-label={`Repère gradué de ${n(b.min)} à ${n(b.max)} sur chaque axe.`}
          data-testid="bs-figure"
          {...drag.svgProps}
        >
          <PlotGrid frame={frame} step={1} />
          <HaloText
            x={o.x - 8}
            y={o.y + 16}
            fontSize={13}
            fontWeight={700}
            fill="var(--color-ink-700)"
          >
            O
          </HaloText>

          {mode === 'battle' &&
            shots.map((s, i) => {
              const p = px(s);
              return isHit(fleet, s) ? (
                <g key={i} stroke={HIT} strokeWidth={3} strokeLinecap="round">
                  <line x1={p.x - 6} y1={p.y - 6} x2={p.x + 6} y2={p.y + 6} />
                  <line x1={p.x - 6} y1={p.y + 6} x2={p.x + 6} y2={p.y - 6} />
                </g>
              ) : (
                <circle key={i} cx={p.x} cy={p.y} r={5} fill="none" stroke={MISS} strokeWidth={2} />
              );
            })}
          {mode === 'battle' &&
            (gameOver ? fleet : sunk).map((s, i) => {
              const a = px(s.cells[0]!);
              const z = px(s.cells[s.cells.length - 1]!);
              return (
                <line
                  key={`s${i}`}
                  x1={a.x}
                  y1={a.y}
                  x2={z.x}
                  y2={z.y}
                  stroke={HIT}
                  strokeOpacity={0.35}
                  strokeWidth={14}
                  strokeLinecap="round"
                />
              );
            })}

          {mode === 'read' && (
            <g>
              <circle cx={px(target).x} cy={px(target).y} r={7} fill={TARGET} />
              <HaloText
                x={px(target).x + 10}
                y={px(target).y - 10}
                fontSize={15}
                fontWeight={700}
                fill={TARGET}
              >
                P
              </HaloText>
            </g>
          )}
          {mode === 'place' && given && !solved && (
            <circle
              cx={px(given.p).x}
              cy={px(given.p).y}
              r={6}
              fill="none"
              stroke={HIT}
              strokeWidth={2}
              strokeDasharray="3 2"
            />
          )}
          {mode === 'place' && (reveal || solved) && (
            <g>
              <circle cx={px(target).x} cy={px(target).y} r={7} fill={TARGET} />
              <HaloText
                x={px(target).x + 10}
                y={px(target).y - 10}
                fontSize={15}
                fontWeight={700}
                fill={TARGET}
              >
                A
              </HaloText>
            </g>
          )}
          {usesCursor && !(mode === 'place' && solved) && (
            <DragHandle
              x={px(cursor).x}
              y={px(cursor).y}
              color={CURSOR}
              name={`${mode === 'battle' ? 'Viseur' : 'Point à placer'} ${coordText(cursor)}`}
              active={drag.active === 'c'}
              keyProps={drag.keyProps('c')}
            />
          )}
        </svg>
        <p className="text-ink-600 mt-1 text-center text-xs font-semibold">
          {'→ abscisse (axe horizontal) · ↑ ordonnée (axe vertical) · O\u00a0: origine'}
        </p>
      </div>

      {mode === 'place' && (
        <div className="flex flex-wrap items-center gap-2">
          {!solved ? (
            <>
              <button type="button" className={PRIMARY_BUTTON} onClick={() => check(cursor)}>
                Valider
              </button>
              {given && (
                <button type="button" className={SECONDARY_BUTTON} onClick={() => setReveal(true)}>
                  Voir la réponse
                </button>
              )}
            </>
          ) : (
            <button type="button" className={PRIMARY_BUTTON} onClick={nextTarget}>
              Point suivant
            </button>
          )}
        </div>
      )}

      {(mode === 'read' || mode === 'battle') && (
        <div className="grid grid-cols-2 gap-2">
          <Stepper
            label="Abscisse"
            value={mode === 'read' ? answer.x : cursor.x}
            min={b.min}
            max={b.max}
            format={(v) => String(v).replace('-', '−')}
            onChange={(v) =>
              mode === 'read' ? setAnswer((a) => ({ ...a, x: v })) : moveCursor({ ...cursor, x: v })
            }
          />
          <Stepper
            label="Ordonnée"
            value={mode === 'read' ? answer.y : cursor.y}
            min={b.min}
            max={b.max}
            format={(v) => String(v).replace('-', '−')}
            onChange={(v) =>
              mode === 'read' ? setAnswer((a) => ({ ...a, y: v })) : moveCursor({ ...cursor, y: v })
            }
          />
        </div>
      )}

      {mode === 'read' && (
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-ink-900 font-display text-lg font-bold tabular-nums">
            P{coordText(answer)}
          </p>
          {!solved ? (
            <button
              type="button"
              className={cn(PRIMARY_BUTTON, 'ml-auto')}
              onClick={() => check(answer)}
            >
              Valider
            </button>
          ) : (
            <button type="button" className={cn(PRIMARY_BUTTON, 'ml-auto')} onClick={nextTarget}>
              Point suivant
            </button>
          )}
        </div>
      )}

      {mode === 'battle' && (
        <div className="flex flex-wrap items-center gap-2">
          {!gameOver ? (
            <button type="button" className={PRIMARY_BUTTON} onClick={shoot}>
              Tirer en {coordText(cursor)}
            </button>
          ) : (
            <button
              type="button"
              className={PRIMARY_BUTTON}
              onClick={() => {
                setFleet(placeFleet(rng.current, b));
                setShots([]);
                setLastShot(null);
              }}
            >
              Nouvelle partie
            </button>
          )}
          <p className="text-ink-600 text-sm" data-testid="bs-count">
            {shots.length} tir{shots.length > 1 ? 's' : ''} · {sunk.length}/{fleet.length} coulé
            {sunk.length > 1 ? 's' : ''}
          </p>
        </div>
      )}

      {mode !== 'battle' && given && (
        <Note tone={given.f === 'ok' ? 'success' : 'error'}>
          <span data-testid="bs-feedback">
            {message(given.f, target, given.p, mode === 'place' ? 'placé' : 'écrit')}
          </span>
        </Note>
      )}
      {mode === 'battle' && lastShot && (
        <Note tone={lastShot.r === 'miss' || lastShot.r === 'repeat' ? 'neutral' : 'success'}>
          <span data-testid="bs-shot">
            {gameOver
              ? `Toute la flotte est coulée en ${shots.length} tirs !`
              : `En ${coordText(lastShot.p)} : ${SHOT_TEXT[lastShot.r]}`}
          </span>
        </Note>
      )}
    </div>
  );
}
